import type {
  IntacctVendor, SubcontractRequest, SubcontractRequestListItem, SubcontractRequestPatch, SubcontractRequestPayload,
  SubcontractRequestPrefill, LdFrequency, ContractBasis, NteRateUnit,
} from '@/lib/apiClient';
import type { ComboboxOption } from '@/components/Combobox';

/**
 * What the subcontract request screens (#627) are built from, kept free of React so the rules are testable.
 *
 * ⚠️ THE API DECIDES WHAT IS COMPLETE, AND WHAT THE PERSON MAY DO. `SubcontractRequest.missing` is the list of what a
 * submit still needs, and `access` says which buttons work; nothing here recomputes either. This file arranges a
 * form, turns it back into the request the API takes, and labels what came back. The checks it does make
 * (`validateRequestForm`) are only the ones that save a round trip on an obvious slip: the API checks every save
 * again and its message is shown as it is.
 *
 * Nothing here trims or tidies what was typed beyond the edges of a field: the Word engine writes exactly what is
 * stored, so a value is sent as it was entered.
 */

// ── The form ─────────────────────────────────────────────────────────────────

/** Yes, No, or not answered yet. A question left unanswered is not the same as No, and the API will not submit one. */
export type YesNo = '' | 'yes' | 'no';

export interface RequestForm {
  templateKey: string;

  vendorName: string; vendorStreet: string; vendorCity: string; vendorState: string; vendorZip: string;
  contactName: string; contactEmail: string; contactPhone: string; newVendor: boolean;

  jobName: string; jobAddress: string; jobCity: string; jobState: string; jobZip: string;
  owner: string; architect: string; costCode: string;

  agreementDate: string; amount: string; retainagePercent: string; billingDay: string;
  /** Always one of the two: a request that stores none is a lump sum. The rate is read only while it is time and material. */
  contractBasis: ContractBasis; nteRate: string; nteRateUnit: '' | NteRateUnit;
  scope: string; exclusions: string;

  prevailingWage: YesNo;
  performanceBond: YesNo; performanceBondAmount: string;
  paymentBond: YesNo; paymentBondAmount: string;
  startDate: string; startAsDirected: boolean;
  completionDate: string; completionAsDirected: boolean;
  liquidatedDamages: YesNo; liquidatedDamagesAmount: string; liquidatedDamagesFrequency: '' | LdFrequency;

  /** By question key. A key with no answer is ''. */
  requirements: Record<string, YesNo>;
  descriptions: { tests: string; certifications: string; inspections: string };
}

export const LD_FREQUENCY_OPTIONS: readonly { key: LdFrequency; label: string }[] = [
  { key: 'day', label: 'Per day' },
  { key: 'week', label: 'Per week' },
  { key: 'month', label: 'Per month' },
];

export const CONTRACT_BASIS_OPTIONS: readonly { key: ContractBasis; label: string }[] = [
  { key: 'lump-sum', label: 'Lump sum' },
  { key: 'time-and-material', label: 'Time and material, not to exceed' },
];

export const NTE_RATE_UNIT_OPTIONS: readonly { key: NteRateUnit; label: string }[] = [
  { key: 'hour', label: 'Per hour' },
  { key: 'day', label: 'Per day' },
  { key: 'week', label: 'Per week' },
];

/** Whether the price is a ceiling (#694). A request that stores no basis is a lump sum. */
export const isTimeAndMaterial = (basis: ContractBasis | undefined): boolean => basis === 'time-and-material';

/**
 * Which Yes/No question each "Describe…" line follows. The line is only worth printing when its question is
 * answered Yes, so the form shows it then and the request stores it only then.
 */
export const DESCRIBED_BY = {
  testing: 'tests',
  certification: 'certifications',
  inspections: 'inspections',
} as const;
export type DescriptionKey = (typeof DESCRIBED_BY)[keyof typeof DESCRIBED_BY];

export const DESCRIPTION_LABELS: Record<DescriptionKey, string> = {
  tests: 'Describe the tests required',
  certifications: 'Describe the certifications required',
  inspections: 'Describe the inspections required',
};

/** Where one request is read, and edited (they are routes of their own so a request has an address to link to). */
export const requestHref = (id: number) => `/contracts/requests/${id}`;

const yn = (b: boolean | undefined): YesNo => (b === true ? 'yes' : b === false ? 'no' : '');
const bool = (v: YesNo): boolean | null => (v === 'yes' ? true : v === 'no' ? false : null);
const str = (v: string | undefined | null): string => v ?? '';

/** Nothing filled in, with an answer slot for every question the contract asks. */
function blankForm(questions: { key: string }[]): RequestForm {
  return {
    templateKey: '',
    vendorName: '', vendorStreet: '', vendorCity: '', vendorState: '', vendorZip: '',
    contactName: '', contactEmail: '', contactPhone: '', newVendor: false,
    jobName: '', jobAddress: '', jobCity: '', jobState: '', jobZip: '', owner: '', architect: '', costCode: '',
    agreementDate: '', amount: '', retainagePercent: '', billingDay: '',
    contractBasis: 'lump-sum', nteRate: '', nteRateUnit: '',
    scope: '', exclusions: '',
    prevailingWage: '',
    performanceBond: '', performanceBondAmount: '', paymentBond: '', paymentBondAmount: '',
    startDate: '', startAsDirected: false, completionDate: '', completionAsDirected: false,
    liquidatedDamages: '', liquidatedDamagesAmount: '', liquidatedDamagesFrequency: '',
    requirements: Object.fromEntries(questions.map((q) => [q.key, '' as YesNo])),
    descriptions: { tests: '', certifications: '', inspections: '' },
  };
}

/**
 * A new request's form: the job's facts as the project holds them, today as the agreement date, and the template
 * the owner and state suggest (blank when nothing decides it, so the person has to choose).
 */
export function emptyRequestForm(prefill: SubcontractRequestPrefill): RequestForm {
  const j = prefill.job;
  return {
    ...blankForm(prefill.requirements),
    templateKey: prefill.suggestedTemplate ?? '',
    jobName: str(j.name), jobAddress: str(j.address), jobCity: str(j.city), jobState: str(j.state), jobZip: str(j.zip),
    owner: str(j.owner),
    agreementDate: prefill.defaults.agreementDate,
  };
}

/** The form for a request that already exists: exactly what it stores. */
export function requestFormFrom(req: SubcontractRequest, prefill: SubcontractRequestPrefill): RequestForm {
  const p = req.payload;
  return {
    ...blankForm(prefill.requirements),
    templateKey: req.templateKey,
    vendorName: str(p.vendorName), vendorStreet: str(p.vendorStreet), vendorCity: str(p.vendorCity),
    vendorState: str(p.vendorState), vendorZip: str(p.vendorZip),
    contactName: str(p.contactName), contactEmail: str(p.contactEmail), contactPhone: str(p.contactPhone),
    newVendor: p.newVendor === true,
    jobName: str(p.jobName), jobAddress: str(p.jobAddress), jobCity: str(p.jobCity), jobState: str(p.jobState),
    jobZip: str(p.jobZip), owner: str(p.owner), architect: str(p.architect), costCode: str(p.costCode),
    agreementDate: str(p.agreementDate), amount: str(p.amount), retainagePercent: str(p.retainagePercent),
    billingDay: p.billingDay === undefined ? '' : String(p.billingDay),
    contractBasis: p.contractBasis ?? 'lump-sum', nteRate: str(p.nteRate), nteRateUnit: p.nteRateUnit ?? '',
    scope: str(p.scope), exclusions: str(p.exclusions),
    prevailingWage: yn(p.prevailingWage),
    performanceBond: yn(p.performanceBond), performanceBondAmount: str(p.performanceBondAmount),
    paymentBond: yn(p.paymentBond), paymentBondAmount: str(p.paymentBondAmount),
    startDate: str(p.startDate), startAsDirected: p.startAsDirected === true,
    completionDate: str(p.completionDate), completionAsDirected: p.completionAsDirected === true,
    liquidatedDamages: yn(p.liquidatedDamages), liquidatedDamagesAmount: str(p.liquidatedDamagesAmount),
    liquidatedDamagesFrequency: p.liquidatedDamagesFrequency ?? '',
    requirements: Object.fromEntries(prefill.requirements.map((r) => [r.key, yn(p.requirements?.[r.key])])),
    descriptions: {
      tests: str(p.descriptions?.tests), certifications: str(p.descriptions?.certifications), inspections: str(p.descriptions?.inspections),
    },
  };
}

// ── The form back into a request ─────────────────────────────────────────────

/** A blank field is "nothing", which the API reads as clearing it. */
const textOrNull = (s: string): string | null => (s.trim() === '' ? null : s);

/**
 * Every document field the form holds, as the API takes it: a blank is `null`, a Yes/No that was not answered is
 * `null`. A field that does not apply (a bond's amount when there is no bond, a "Describe…" line whose question
 * is not Yes) is `null` too, so what is stored never disagrees with the answers beside it.
 *
 * Written out key by key against the patch type on purpose: a misspelled key or a value of the wrong type is a
 * compile error here, not a 400 ("Unknown field") on somebody's save.
 */
export function requestPayload(f: RequestForm): SubcontractRequestPatch {
  const yes = (answer: YesNo, value: string): string | null => (answer === 'yes' ? textOrNull(value) : null);
  // A lump sum stores no basis at all (that is what absent means), and no rate: the rate and its unit only apply to time and material.
  const timeAndMaterial = isTimeAndMaterial(f.contractBasis);
  // A "Describe…" line is stored only while its question is answered Yes.
  const descriptions: Record<DescriptionKey, string | null> = { tests: null, certifications: null, inspections: null };
  for (const [question, line] of Object.entries(DESCRIBED_BY)) {
    descriptions[line] = f.requirements[question] === 'yes' ? textOrNull(f.descriptions[line]) : null;
  }
  return {
    vendorName: textOrNull(f.vendorName), vendorStreet: textOrNull(f.vendorStreet), vendorCity: textOrNull(f.vendorCity),
    vendorState: textOrNull(f.vendorState), vendorZip: textOrNull(f.vendorZip),
    contactName: textOrNull(f.contactName), contactEmail: textOrNull(f.contactEmail), contactPhone: textOrNull(f.contactPhone),
    newVendor: f.newVendor,

    jobName: textOrNull(f.jobName), jobAddress: textOrNull(f.jobAddress), jobCity: textOrNull(f.jobCity),
    jobState: textOrNull(f.jobState), jobZip: textOrNull(f.jobZip),
    owner: textOrNull(f.owner), architect: textOrNull(f.architect), costCode: textOrNull(f.costCode),

    agreementDate: textOrNull(f.agreementDate), amount: textOrNull(f.amount), retainagePercent: textOrNull(f.retainagePercent),
    billingDay: f.billingDay.trim() === '' ? null : Number(f.billingDay),
    contractBasis: timeAndMaterial ? 'time-and-material' : null,
    nteRate: timeAndMaterial ? textOrNull(f.nteRate) : null,
    // A unit means nothing without its rate (the API lists one half alone as missing), so removing the rate removes the unit too.
    nteRateUnit: timeAndMaterial && textOrNull(f.nteRate) !== null && f.nteRateUnit !== '' ? f.nteRateUnit : null,
    scope: textOrNull(f.scope), exclusions: textOrNull(f.exclusions),

    prevailingWage: bool(f.prevailingWage),
    performanceBond: bool(f.performanceBond), performanceBondAmount: yes(f.performanceBond, f.performanceBondAmount),
    paymentBond: bool(f.paymentBond), paymentBondAmount: yes(f.paymentBond, f.paymentBondAmount),
    startDate: textOrNull(f.startDate), startAsDirected: f.startAsDirected,
    completionDate: textOrNull(f.completionDate), completionAsDirected: f.completionAsDirected,
    liquidatedDamages: bool(f.liquidatedDamages),
    liquidatedDamagesAmount: yes(f.liquidatedDamages, f.liquidatedDamagesAmount),
    liquidatedDamagesFrequency: f.liquidatedDamages === 'yes' && f.liquidatedDamagesFrequency !== '' ? f.liquidatedDamagesFrequency : null,

    requirements: Object.fromEntries(Object.entries(f.requirements).map(([k, v]) => [k, bool(v)])),
    descriptions,
  };
}

/** The three plain tick boxes: unticked means "not said", so a new request leaves it out. (A Yes/No answer of No is sent.) */
const TICK_KEYS = ['newVendor', 'startAsDirected', 'completionAsDirected'];

/** The same, for a new request: a field with nothing in it is left out rather than sent as a clear. */
export function createBody(f: RequestForm, projectId: number): { projectId: number; templateKey: string; payload: SubcontractRequestPatch } {
  const all = requestPayload(f) as Record<string, unknown>;
  const payload: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(all)) {
    if (k === 'requirements' || k === 'descriptions') {
      const kept = Object.fromEntries(Object.entries(v as Record<string, unknown>).filter(([, x]) => x !== null));
      if (Object.keys(kept).length > 0) payload[k] = kept;
    } else if (v !== null && !(v === false && TICK_KEYS.includes(k))) {
      payload[k] = v;
    }
  }
  return { projectId, templateKey: f.templateKey, payload: payload as SubcontractRequestPatch };
}

/**
 * Only what changed since the form opened: a colleague's edit to another field, made while this was open,
 * survives, exactly as in the other Contracts forms. A cleared field is sent as `null`. Empty when nothing changed.
 */
export function updatePatch(initial: RequestForm, current: RequestForm): { templateKey?: string; payload?: SubcontractRequestPatch } {
  const before = requestPayload(initial) as Record<string, unknown>;
  const after = requestPayload(current) as Record<string, unknown>;
  const payload: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(after)) {
    if (k === 'requirements' || k === 'descriptions') {
      const b = (before[k] ?? {}) as Record<string, unknown>;
      const changed = Object.fromEntries(Object.entries(v as Record<string, unknown>).filter(([key, x]) => x !== b[key]));
      if (Object.keys(changed).length > 0) payload[k] = changed;
    } else if (v !== before[k]) {
      payload[k] = v;
    }
  }
  const out: { templateKey?: string; payload?: SubcontractRequestPatch } = {};
  if (current.templateKey !== initial.templateKey) out.templateKey = current.templateKey;
  if (Object.keys(payload).length > 0) out.payload = payload as SubcontractRequestPatch;
  return out;
}

// ── The checks that save a round trip ────────────────────────────────────────

const MONEY_GROUPED = /^\d{1,3}(,\d{3})+(\.\d{1,2})?$/;
const MONEY_PLAIN = /^\d+(\.\d{1,2})?$/;

/** The API's reading of a dollar amount: "4680", "4,680.50" and "$4,680" are amounts, "1,23" is a typo. */
export function isMoney(input: string): boolean {
  const s = input.trim().replace(/^\$\s*/, '');
  return MONEY_GROUPED.test(s) || MONEY_PLAIN.test(s);
}

function isPercent(input: string): boolean {
  const s = input.trim().replace(/\s*%$/, '');
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(s)) return false;
  const n = Number(s);
  return n >= 0 && n <= 100;
}

/**
 * The first slip worth stopping on before the round trip, or null. A draft may be half-filled, so nothing here
 * requires a field: it only refuses a value that cannot be what was meant, and the template, which has to be chosen.
 */
export function validateRequestForm(f: RequestForm): string | null {
  if (f.templateKey === '') return 'Choose the Word template this subcontract uses.';
  // An amount the form no longer shows (its question was answered No) is not checked: the person cannot see it to
  // fix it, and `requestPayload` sends nothing for it either.
  const money: [string, string][] = [
    [f.amount, 'The subcontract amount'],
    [f.performanceBond === 'yes' ? f.performanceBondAmount : '', 'The performance bond amount'],
    [f.paymentBond === 'yes' ? f.paymentBondAmount : '', 'The payment bond amount'],
    [f.liquidatedDamages === 'yes' ? f.liquidatedDamagesAmount : '', 'The liquidated damages amount'],
    [isTimeAndMaterial(f.contractBasis) ? f.nteRate : '', 'The not-to-exceed rate'],
  ];
  for (const [value, label] of money) {
    if (value.trim() !== '' && !isMoney(value)) return `${label} must be a dollar amount, like 4,680.00.`;
  }
  if (f.retainagePercent.trim() !== '' && !isPercent(f.retainagePercent)) return 'Retainage must be a percentage from 0 to 100.';
  if (f.billingDay.trim() !== '') {
    const n = Number(f.billingDay);
    if (!/^\d{1,2}$/.test(f.billingDay.trim()) || n < 1 || n > 31) return 'The monthly billing day must be a day of the month, 1 to 31.';
  }
  if (f.startDate !== '' && f.completionDate !== '' && f.completionDate < f.startDate) {
    return 'The project completion date can’t be before the start date.';
  }
  return null;
}

// ── Showing a request ────────────────────────────────────────────────────────

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "2026-07-18" → "July 18, 2026", read as plain parts so no time zone can move the day. */
export function writtenDay(ymd: string | undefined | null): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd ?? '');
  if (!m) return '';
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}

/** "4680.00" → "$4,680.00". Blank stays blank. */
export function moneyDisplay(canonical: string | undefined | null): string {
  if (!canonical) return '';
  const [whole, frac = '00'] = canonical.split('.');
  return `$${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${frac.padEnd(2, '0')}`;
}

function ordinal(n: number): string {
  const teen = n % 100 >= 11 && n % 100 <= 13;
  const suffix = teen ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th';
  return `${n}${suffix}`;
}

const yesNo = (b: boolean | undefined): string => (b === true ? 'Yes' : b === false ? 'No' : '');
const joinAddress = (street?: string, city?: string, state?: string, zip?: string): string => {
  const place = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [street, place].filter(Boolean).join(', ');
};

export interface SummaryRow { label: string; value: string; /** Long text that keeps its line breaks. */ block?: boolean }
export interface SummarySection { title: string; rows: SummaryRow[] }

/**
 * A request, read back in the order of the form, for the page a person reviews before submitting and the
 * coordinator reads. An entry with nothing in it has an empty value, which the screen shows as a dash, so a gap
 * is visible and not silently left out.
 */
export function summarySections(req: SubcontractRequest): SummarySection[] {
  const p: SubcontractRequestPayload = req.payload;
  const timeAndMaterial = isTimeAndMaterial(p.contractBasis);
  const directed = (ymd: string | undefined, asDirected: boolean | undefined) =>
    writtenDay(ymd) === '' ? '' : `${writtenDay(ymd)}${asDirected === true ? ' or As Directed by E&W' : ''}`;
  const sections: SummarySection[] = [
    { title: 'Job', rows: [
      { label: 'Job name', value: str(p.jobName) },
      { label: 'Address', value: joinAddress(p.jobAddress, p.jobCity, p.jobState, p.jobZip) },
      { label: 'Owner', value: str(p.owner) },
      { label: 'Architect', value: str(p.architect) },
      { label: 'Cost code', value: str(p.costCode) },
    ] },
    { title: 'Subcontractor', rows: [
      { label: 'Name', value: str(p.vendorName) + (p.newVendor === true ? ' (new vendor)' : '') },
      { label: 'Address', value: joinAddress(p.vendorStreet, p.vendorCity, p.vendorState, p.vendorZip) },
      { label: 'Contact', value: str(p.contactName) },
      { label: 'Email', value: str(p.contactEmail) },
      { label: 'Phone', value: str(p.contactPhone) },
    ] },
    { title: 'Agreement', rows: [
      { label: 'Template', value: req.templateLabel },
      { label: 'Agreement date', value: writtenDay(p.agreementDate) },
      { label: timeAndMaterial ? 'Not-to-exceed amount' : 'Amount', value: moneyDisplay(p.amount) },
      { label: 'Contract basis', value: timeAndMaterial ? 'Time and material, not to exceed' : 'Lump sum' },
      ...(timeAndMaterial ? [{ label: 'Not-to-exceed rate', value: p.nteRate ? `${moneyDisplay(p.nteRate)}${p.nteRateUnit ? ` per ${p.nteRateUnit}` : ''}` : '' }] : []),
      { label: 'Retainage', value: p.retainagePercent === undefined ? '' : `${p.retainagePercent}%` },
      { label: 'Monthly billing date', value: p.billingDay === undefined ? '' : ordinal(p.billingDay) },
    ] },
    { title: 'Scope of work', rows: [
      { label: 'Scope includes', value: str(p.scope), block: true },
      { label: 'Excluded from scope', value: str(p.exclusions), block: true },
    ] },
    { title: 'Payroll and bonds', rows: [
      { label: 'Prevailing wage project', value: yesNo(p.prevailingWage) },
      { label: 'Performance bond', value: yesNo(p.performanceBond) + (p.performanceBond === true && p.performanceBondAmount ? `, ${moneyDisplay(p.performanceBondAmount)}` : '') },
      { label: 'Payment bond', value: yesNo(p.paymentBond) + (p.paymentBond === true && p.paymentBondAmount ? `, ${moneyDisplay(p.paymentBondAmount)}` : '') },
    ] },
    { title: 'Schedule', rows: [
      { label: 'Project start date', value: directed(p.startDate, p.startAsDirected) },
      { label: 'Project completion date', value: directed(p.completionDate, p.completionAsDirected) },
      {
        label: 'Liquidated damages',
        value: yesNo(p.liquidatedDamages) + (p.liquidatedDamages === true && p.liquidatedDamagesAmount
          ? `, ${moneyDisplay(p.liquidatedDamagesAmount)}${p.liquidatedDamagesFrequency ? ` per ${p.liquidatedDamagesFrequency}` : ''}` : ''),
      },
    ] },
    { title: 'Requirements', rows: req.requirements.map((r) => ({ label: r.label, value: yesNo(p.requirements?.[r.key]) })) },
  ];
  for (const [question, line] of Object.entries(DESCRIBED_BY)) {
    const text = p.descriptions?.[line];
    if (p.requirements?.[question] === true && text) sections[sections.length - 1].rows.push({ label: DESCRIPTION_LABELS[line], value: text, block: true });
  }
  return sections;
}

/** Where a request stands, in a word, and which of the three chip colors it takes. */
export function requestStanding(r: Pick<SubcontractRequestListItem, 'status' | 'numbered' | 'subcontractNumber' | 'paperworkVoided' | 'paperworkRemoved'>):
  { label: string; tone: 'received' | 'waiting' | 'missing' } {
  if (r.status === 'draft') return { label: 'Draft', tone: 'waiting' };
  if (r.status === 'withdrawn') return { label: 'Withdrawn', tone: 'waiting' };
  if (r.paperworkVoided) return { label: 'Voided', tone: 'missing' };
  if (r.numbered) return { label: `Number ${r.subcontractNumber}`, tone: 'received' };
  if (r.paperworkRemoved) return { label: 'Taken off the Contracts page', tone: 'missing' };
  return { label: 'Waiting for a number', tone: 'waiting' };
}

// ── Picking the subcontractor from Intacct's vendor list ─────────────────────

/**
 * The seven fields a picked vendor fills besides the name. The name is not one of them: it holds what was typed to find the vendor,
 * so text there is not a reason to ask before replacing.
 */
const VENDOR_BLOCK = ['vendorStreet', 'vendorCity', 'vendorState', 'vendorZip', 'contactName', 'contactEmail', 'contactPhone'] as const;

/** What a pick writes into each of them. A value Intacct has nothing usable for is '' (the field is left blank, not left stale). */
const vendorBlockOf = (v: IntacctVendor): Pick<RequestForm, (typeof VENDOR_BLOCK)[number]> => ({
  vendorStreet: v.street, vendorCity: v.city, vendorState: v.state, vendorZip: v.zip,
  contactName: v.contactName, contactEmail: v.contactEmail, contactPhone: v.contactPhone,
});

/**
 * Would picking a vendor replace something a person typed? True when any of the seven holds text that is not simply what the LAST
 * pick filled in. (Picking a second vendor straight after the first has typed nothing to lose; typing over a field since has.)
 */
export function vendorDetailsTyped(form: RequestForm, lastPicked: IntacctVendor | null): boolean {
  const last = lastPicked ? vendorBlockOf(lastPicked) : null;
  return VENDOR_BLOCK.some((k) => form[k].trim() !== '' && form[k] !== (last?.[k] ?? ''));
}

/**
 * The form after picking a vendor: the whole subcontractor block is the vendor's, so a half-typed address is never left under a
 * different vendor's name. `newVendor` is NOT touched: being in Intacct does not say the new vendor form is done.
 */
export function withVendor(form: RequestForm, v: IntacctVendor): RequestForm {
  return { ...form, vendorName: v.name, ...vendorBlockOf(v) };
}

/** What a row of the list says under the name: the id, which tells two vendors with the same name apart, and where they are. */
export function vendorSub(v: IntacctVendor): string {
  const where = [v.city, v.state].filter(Boolean).join(', ');
  return [v.id, where].filter(Boolean).join(' · ');
}

/** The type-ahead's options. `key` is the vendor id: two vendors can share a name, and the form finds the pick by it. */
export function vendorOptions(vendors: IntacctVendor[]): ComboboxOption[] {
  return vendors.map((v) => ({ key: v.id, value: v.name, sub: vendorSub(v) }));
}

/** What the line under the name says after a pick: where the details came from, and what could not come with them. */
export function vendorFilledNote(v: IntacctVendor): string {
  const abroad = v.abroad
    ? ` Intacct has this vendor in ${v.country || 'another country'}, and the form holds a US state and zip only, so those two are blank.`
    : '';
  return `Filled from Intacct vendor ${v.id}. Check each field before you submit.${abroad}`;
}

// ── Documents to include with a request (#656) ───────────────────────────────

/** Mirrors SUBCONTRACT_REQUEST_FILE_MAX_BYTES in the API. Checked here only to say so before uploading. */
export const REQUEST_FILE_MAX_BYTES = 15 * 1024 * 1024;

/** Mirrors SUBCONTRACT_REQUEST_FILES_MAX in the API: how many documents one request may carry. */
export const REQUEST_FILES_MAX = 10;

/** What the file picker offers. The API's allowlist is the real check. */
export const FILE_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv';

/**
 * Whether a file name ends in a type the API accepts, ignoring case. The API checks the NAME as well as the type the browser
 * declares (a file named `invoice.exe` can declare itself a PDF, and the coordinator opens downloads under their names), so this
 * says so before the upload instead of after. The endings are the picker's own list, so there is one list on this side.
 */
export function hasAllowedEnding(name: string): boolean {
  const ending = /\.([A-Za-z0-9]+)$/.exec(name.trim());
  return !!ending && FILE_ACCEPT.split(',').includes(`.${ending[1].toLowerCase()}`);
}

/** Why `file` can't be uploaded, or null if it can. */
export function fileProblem(file: { name: string; size: number }): string | null {
  if (!hasAllowedEnding(file.name)) return 'That file type can’t be attached. Allowed: images, PDF, Word/Excel/PowerPoint, text/CSV.';
  if (file.size === 0) return 'That file is empty.';
  if (file.size > REQUEST_FILE_MAX_BYTES) return 'That file is larger than the 15 MB limit.';
  return null;
}

/**
 * Split a batch into the files that can upload and a message for each that can't, named, so dropping five files and losing one
 * says which one. `room` is how many more the request can take: a file past it is named too, not silently dropped.
 */
export function sortUploads<F extends { name: string; size: number }>(files: F[], room: number): { ok: F[]; problems: string[] } {
  const ok: F[] = [];
  const problems: string[] = [];
  for (const f of files) {
    const own = fileProblem(f);
    if (own) problems.push(`“${f.name}”: ${own}`);
    else if (ok.length >= Math.max(room, 0)) problems.push(`“${f.name}”: A request can carry at most ${REQUEST_FILES_MAX} documents.`);
    else ok.push(f);
  }
  return { ok, problems };
}

/**
 * Whether a drag carries files from the computer, rather than selected text or a link. Array.from, because older browsers hand
 * back a DOMStringList, which has no includes().
 */
export function carriesFiles(dt: { types?: ArrayLike<string> | null } | null | undefined): boolean {
  return Array.from(dt?.types ?? []).includes('Files');
}

export function fmtSize(bytes: number | null): string | null {
  if (bytes == null) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
